import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button, TextField } from 'components';
import {
	defaultResetPasswordFormInputValues,
	loginFormSchema,
	passwordChecklistItems,
	resetPasswordFormSchema,
} from 'models/authentication';
import { AuthPage } from 'pages/Auth';
import { clearSessionCache, getOktaErrorMessage } from 'utils/authentication';
import { Routes } from 'models/navigation';
import { useAuth, useToast } from 'context';
import { getQueryError, handleApiResponse, useResetPasswordMutation } from 'store';

import type { AnyAuthContext } from 'context';
import type { LoginFormInputs } from 'models/authentication';
import type { SubmitHandler } from 'react-hook-form';

interface ResetPasswordFormInputs {
	email: string;
	password: string;
	confirmPassword: string; // Added field
}

const resetPasswordSchema = yup.object({
	email: loginFormSchema.fields.email,
	password: loginFormSchema.fields.password,
	confirmPassword: yup
		.string()
		.required('Please retype your password.')
		.oneOf([yup.ref('password')], 'Your passwords do not match.'),
});

export function ResetPassword() {
	const auth = useAuth<AnyAuthContext>();
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		reset,
		getValues,
		setValue,
		watch,
		formState: { errors, isDirty, isSubmitting, isSubmitted },
	} = useForm<ResetPasswordFormInputs>({
		resolver: yupResolver(resetPasswordSchema),
	});

	const [errorMessage, setErrorMessage] = useState('');
	const [formValues, setFormValues] = useState({
		email: '',
		password: '',
		confirmPassword: '',
	});
	const [resetPassword] = useResetPasswordMutation();
	const toast = useToast();

	const token = new URLSearchParams(window.location.search).get('token') ?? '';
	const query_email = new URLSearchParams(window.location.search).get('email') ?? '';

	// set form defaults to user provided query params
	useEffect(() => {
		setValue('email', query_email);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query_email]);

	const onSubmit: SubmitHandler<ResetPasswordFormInputs> = async (formData) => {
		const response = await resetPassword({
			password: formData.password,
			token: token,
		});

		handleApiResponse(response, {
			success: (payload) => {
				toast.createToast({
					title: `Log in with your new password!`,
					variant: 'success',
				});
				navigate(Routes.HOME);
			},
			error: (payload) => {
				toast.createToast({
					title: getQueryError(payload),
					variant: 'error',
				});
			},
		});
	};

	const password = watch('password'); // Watch password for real-time validation
	const confirmPassword = watch('confirmPassword');

	// When an authentication error occurs, keep the form's values but reset its
	// dirty state. Then when the form becomes dirty again, clear the error since
	// the user has indicated they are trying again.
	useEffect(() => {
		const clearAuthError = auth.clearError;
		if (auth.status === 'api_error') {
			if (isSubmitted) {
				reset(
					{
						email: getValues('email'),
						password: '',
						confirmPassword: '',
					},
					{ keepValues: true }
				);
			} else if (isDirty) {
				clearAuthError();
			}
		}
	}, [auth.status, auth.clearError, isSubmitted, isDirty, reset]);

	// Provide a more detailed authentication error message based on the error
	// code returned by the Okta API.
	useEffect(() => {
		if (auth.status === 'api_error') {
			setErrorMessage(getOktaErrorMessage(auth.data.error.errorCode));
		} else {
			setErrorMessage('');
		}
	}, [auth.status, auth.data]);

	// clear cache once on comp load
	useEffect(() => {
		clearSessionCache();
	}, []);

	return (
		<AuthPage>
			<div className='flex'>
				<div className='flex max-w-xl w-full'>
					<div className='m-auto pt-7 px-8 max-w-md w-full'>
						<h1 className='font-semibold text-h4'>Submit Your New Password</h1>
						<form className='flex flex-col mt-8' onSubmit={handleSubmit(onSubmit)} noValidate autoComplete='off'>
							<TextField
								containerProps={{ className: 'my-2' }}
								label='Create Password'
								type='password'
								sizeY='md'
								errorMessage={errors.password?.message}
								onInput={() => {
									setFormValues({
										...formValues,
										password: getValues('password'),
									});
								}}
								{...register('password')}
							/>

							<TextField
								containerProps={{ className: 'my-2' }}
								label='Confirm Password'
								type='password'
								sizeY='md'
								errorMessage={errors.confirmPassword?.message}
								onInput={() => {
									setFormValues({
										...formValues,
										confirmPassword: getValues('confirmPassword'),
									});
								}}
								{...register('confirmPassword')}
							/>

							{/* Password Checklist */}
							<div className='my-2'>
								{passwordChecklistItems.map((item, index) => (
									<div key={index} className='flex my-2 text-p2'>
										<div className='flex-initial w-8'>
											<span
												className={classNames(
													'material-symbol relative -top-[2px]',
													item.test(getValues('password'), getValues('email')) ? 'text-green-500' : 'text-gray-400'
												)}
											>
												check_circle
											</span>
										</div>
										<div className='grow'>{item.text}</div>
									</div>
								))}

								{/* Password Match Check */}
								<div className='flex my-2 text-p2'>
									<div className='flex-initial w-8'>
										<span
											className={classNames(
												'material-symbol relative -top-[2px]',
												password && confirmPassword === password ? 'text-green-500' : 'text-gray-400'
											)}
										>
											check_circle
										</span>
									</div>
									<div className='grow'>Passwords match</div>
								</div>
							</div>

							{/* Submit Button */}
							<Button
								className='mt-12'
								sizeY='lg'
								isWorking={isSubmitting}
								disabled={!isDirty || !!errors.password || password !== confirmPassword}
							>
								{isSubmitting ? 'Submitting' : 'Submit'}
							</Button>

							{/* Error Message */}
							<p className='mt-2 text-p2 text-red-400'>{errorMessage || <>&nbsp;</>}</p>

							{/* Back to Login Link */}
							<div>
								<Link to={Routes.HOME} className='self-start font-secondary text-p2'>
									<span className='material-symbol-sm relative top-[5px] mr-1'>keyboard_backspace</span>
									Back to Login
								</Link>
							</div>
						</form>
					</div>
				</div>
			</div>
			<div className='bg-merlinBlue'></div>
		</AuthPage>
	);
}

export default ResetPassword;
